import React from 'react';
import { colors } from './theme';
import { analyticsCardStyles as card } from './analytics/analyticsCardStyles';

function DoughnutChart({ data, colorMap }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.count, 0);
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;
  return (
    <div style={{ position: 'relative', width: '100px', height: '100px' }}>
      <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
        {data.map(d => {
          const dash = (d.count / total) * circumference;
          const dashArray = `${dash} ${circumference - dash}`;
          const circle = (
            <circle
              key={d._id}
              r={radius}
              cx="50"
              cy="50"
              fill="transparent"
              stroke={colorMap[d._id] || colors.accent}
              strokeWidth="20"
              strokeDasharray={dashArray}
              strokeDashoffset={-(cumulative / total) * circumference}
            />
          );
          cumulative += d.count;
          return circle;
        })}
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontWeight: 'bold', color: colors.textStrong }}>
        {total}
      </div>
    </div>
  );
}

function MiniBarChart({ data, colorMap }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count));
  return (
    <div style={{ marginTop: '0.5rem' }}>
      {data.map(item => (
        <div key={item._id} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colorMap[item._id] || colors.muted, marginRight: '0.25rem' }}></span>
          <span style={{ flex: '0 0 84px', fontSize: '0.75rem', color: colors.textDim }}>{item._id}</span>
          <div style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: '999px', overflow: 'hidden', marginRight: '0.25rem' }}>
            <div style={{ height: '6px', width: `${(item.count / max) * 100}%`, backgroundColor: colorMap[item._id] || colors.accent }}></div>
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>{item.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function ProductionAnalyticsCard({ stats, year, setYear, years, explain, setExplain }) {
  if (!stats) return null;

  const top = stats.byProductNumber.slice(0, 4);
  const others = stats.byProductNumber.slice(4);
  const accentPalette = [colors.accent, colors.success, colors.statusYellow, '#6c757d', '#17a2b8'];
  const colorMap = {};
  top.forEach((p, idx) => {
    colorMap[p._id] = accentPalette[idx % accentPalette.length];
  });
  if (others.length > 0) {
    colorMap['Others'] = '#cccccc';
  }
  const chartData = [...top];
  if (others.length > 0) {
    const count = others.reduce((s, d) => s + d.count, 0);
    chartData.push({ _id: 'Others', count });
  }

  const scopeText =
    year === 'total'
      ? 'Scope: Total (All Years)'
      : (() => {
          const start = new Date(Date.UTC(year, 0, 1));
          const end = new Date(Date.UTC(year, 11, 31));
          const fmt = d => d.toISOString().split('T')[0];
          return `Scope: ${year} (${fmt(start)}–${fmt(end)} UTC)`;
        })();

  return (
    <div style={{ ...card.shell, maxWidth: '760px' }}>
      <div style={card.header}>
        <div style={card.titleWrap}>
          <p style={card.eyebrow}>Device mix</p>
          <h2 style={card.title}>Production Data Analytics</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <label style={{ fontSize: '0.9rem', color: '#11201c', fontWeight: 700 }}>
            Year:
            <select className="ni-select" value={year} onChange={e => setYear(e.target.value)} style={{ marginLeft: '0.5rem', minWidth: '110px' }}>
              <option value="total">Total</option>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          {process.env.NODE_ENV !== 'production' && (
            <label style={{ fontSize: '0.9rem', marginLeft: '0.5rem', color: '#11201c', fontWeight: 700 }}>
              <input
                type="checkbox"
                checked={explain}
                onChange={e => setExplain(e.target.checked)}
                style={{ marginRight: '0.25rem' }}
              />
              Explain
            </label>
          )}
        </div>
      </div>
      <div style={card.body}>
        <p style={{ margin: '0 0 0.75rem 0', color: colors.textDim, fontSize: '0.85rem' }}>{scopeText}</p>
        <div style={card.statRow}>
          <span style={card.statLabel}>Total Devices</span>
          <strong style={card.statValue}>{stats.total}</strong>
        </div>
        <section style={{ ...card.section, display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '0 0 120px', display: 'flex', justifyContent: 'center' }}>
            <DoughnutChart data={chartData} colorMap={colorMap} />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {top.map(item => (
                <li key={item._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: colorMap[item._id] }}></span>
                    <span style={{ color: colors.textBody }}>{item._id}</span>
                  </div>
                  <strong style={card.statValue}>{item.count}</strong>
                </li>
              ))}
            </ul>
          </div>
        </section>
        {others.length > 0 && (
          <section style={{ ...card.section, marginTop: '1rem' }}>
            <MiniBarChart data={others} colorMap={colorMap} />
            <p style={{ color: colors.textDim, fontSize: '0.75rem', marginTop: '0.25rem' }}>Total devices in chart: {stats.total}</p>
          </section>
        )}
      </div>
    </div>
  );
}
